import { useState } from "react";
import { Mail, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";

export default function EmailVerifyBanner() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [sending, setSending] = useState(false);

  // Hide for users without an account or already verified. Also hide for Google
  // OAuth users — their email is verified by Google itself, so the banner is
  // never relevant for them (and would mislead).
  const isOAuth = user?.auth_method === "google" || user?.auth_provider === "google" || !!user?.picture;
  if (!user || user.email_verified || isOAuth) return null;

  const resend = async () => {
    setSending(true);
    try {
      const r = await api.post("/auth/resend-confirmation");
      if (r.data?.already_verified) {
        toast.success(t("evb_already_verified"));
      } else {
        toast.success(t("evb_resent_to").replace("{email}", user.email));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("evb_resend_failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="mb-6 rounded-md border border-yellow-500/40 bg-yellow-500/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid="email-verify-banner"
    >
      <Mail className="w-5 h-5 text-yellow-300 shrink-0" />
      <div className="flex-1">
        <div className="text-yellow-100 text-sm font-medium mb-1">
          {t("evb_title")}
        </div>
        <p className="text-yellow-200/80 text-xs leading-relaxed">
          {t("evb_body_prefix")} <strong className="text-yellow-100">{user.email}</strong>. {t("evb_body_suffix")}
        </p>
      </div>
      <button
        onClick={resend}
        disabled={sending}
        className="btn-ghost text-xs whitespace-nowrap"
        data-testid="resend-confirmation-btn"
      >
        {sending ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
        {sending ? t("evb_sending") : t("evb_resend_cta")}
      </button>
    </div>
  );
}
